import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { Glyph } from "@/components/Glyph";
import { ProductImage } from "@/components/ProductImage";
import { AddToCart } from "@/components/AddToCart";
import { useSeo } from "@/lib/seo";
import { useLang, lp, loc } from "@/lib/i18n";

const OR = "#C9A86A";

const T = {
  fr: {
    notFound: "Référence introuvable.", back: "Retour", backTop: "← Crisopea",
    format: "Eau de parfum concentrée · 50 ml · Série limitée numérotée",
    pyramid: "Pyramide", top: "Tête", heart: "Cœur", base: "Fond",
    unpublished: "La composition n'est pas dévoilée pour cette référence.",
    perfumer: "Parfumeur : non communiqué. Seule l'œuvre signe.",
    soldOut: "Épuisé", soldOutNote:
      "Cette référence est épuisée et son retour n'est pas garanti. Laisse ton adresse : tu seras prévenu en premier si elle revient.",
    notify: "Être averti d'un retour", available: "Disponible",
    emailPlaceholder: "Ton adresse e-mail", notifyBtn: "M'avertir",
    made: "Fabriqué en Espagne.",
    seoDesc: (p, metal, planet) =>
      `CRISOPEA ${p.roman}, ${metal} · ${planet}. Eau de parfum concentrée, 50 ml, série limitée numérotée.`,
    altEtui: (p, metal, planet) => `Étui CRISOPEA ${p.roman} — ${metal}, ${planet}`,
    altMacro: (p) => `Le glyphe de CRISOPEA ${p.roman}, gravé dans le métal`,
    altDuo: (p) => `CRISOPEA ${p.roman}, flacon et étui`,
  },
  en: {
    notFound: "Reference not found.", back: "Back", backTop: "← Crisopea",
    format: "Concentrated eau de parfum · 50 ml · Numbered limited series",
    pyramid: "Pyramid", top: "Top", heart: "Heart", base: "Base",
    unpublished: "The composition is not disclosed for this reference.",
    perfumer: "Perfumer: undisclosed. Only the work signs.",
    soldOut: "Sold out", soldOutNote:
      "This reference is sold out and its return is not guaranteed. Leave your address and you will be told first if it comes back.",
    notify: "Be notified of a return", available: "Available",
    emailPlaceholder: "Your e-mail address", notifyBtn: "Notify me",
    made: "Made in Spain.",
    seoDesc: (p, metal, planet) =>
      `CRISOPEA ${p.roman}, ${metal} · ${planet}. Concentrated eau de parfum, 50 ml, numbered limited series.`,
    altEtui: (p, metal, planet) => `CRISOPEA ${p.roman} case — ${metal}, ${planet}`,
    altMacro: (p) => `The glyph of CRISOPEA ${p.roman}, struck in the metal`,
    altDuo: (p) => `CRISOPEA ${p.roman}, bottle and case`,
  },
};

const NotifyForm = ({ productRef, t }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/notify", { email, ref: productRef });
      toast.success(data.message);
      setEmail("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-8 max-w-md" data-testid="notify-form">
      <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-400">{t.notify}</p>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder={t.emailPlaceholder} className="val-input mt-4" data-testid="notify-email" />
      <button type="submit" disabled={busy} className="val-btn mt-6" data-testid="notify-submit">
        {busy ? "…" : t.notifyBtn}
      </button>
    </form>
  );
};

export default function CrisopeaProduct() {
  const { slug } = useParams();
  const lang = useLang();
  const t = T[lang];
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setP(null); setErr(false);
    api.get(`/products/crisopea-${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);

  const metal = p && loc(p, lang, "metal");
  const planet = p && loc(p, lang, "planet");
  const description = p && loc(p, lang, "description");
  const ambiance = p && loc(p, lang, "ambiance");
  const pyramid = p && loc(p, lang, "pyramid");

  useSeo(
    p ? `CRISOPEA ${p.roman} — Maison Partita` : "Crisopea · Maison Partita",
    p ? t.seoDesc(p, metal, planet) : ""
  );

  if (err) return (
    <div className="max-w-3xl mx-auto px-5 py-40 text-center text-zinc-400">
      {t.notFound} <Link to={lp(lang, "/crisopea")} className="val-link underline">{t.back}</Link>
    </div>
  );
  if (!p) return <div className="py-40 text-center text-zinc-600 tracking-[0.2em] uppercase text-[13px]">…</div>;

  const hasFull = (p.images || []).includes("flacon-macro");

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-16 pb-24">
      <Link to={lp(lang, "/crisopea")} className="val-link text-[13px] tracking-[0.2em] uppercase" data-testid="back-link">
        {t.backTop}
      </Link>

      <div className="mt-12 grid gap-16 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden border" style={{ background: "#111", borderColor: "rgba(201,168,106,0.16)" }}>
            <ProductImage maison="crisopea" reference={p.reference} suffix="etui"
              alt={t.altEtui(p, metal, planet)} eager />
          </div>
          {hasFull && (
            <div className="mt-6 grid grid-cols-2 gap-6">
              <div className="overflow-hidden border" style={{ background: "#111", borderColor: "rgba(201,168,106,0.16)" }}>
                <ProductImage maison="crisopea" reference={p.reference} suffix="flacon-macro" alt={t.altMacro(p)} />
              </div>
              <div className="overflow-hidden border" style={{ background: "#111", borderColor: "rgba(201,168,106,0.16)" }}>
                <ProductImage maison="crisopea" reference={p.reference} suffix="duo-blanc" alt={t.altDuo(p)} />
              </div>
            </div>
          )}
        </Reveal>

        <div className="lg:pt-6">
          <Reveal>
            <div className="flex items-center gap-6">
              <Glyph glyph={p.glyph} color={p.color} size={72} faint={!p.available} />
              <div>
                <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{metal} · {planet}</p>
                <h1 className="mt-2 text-3xl sm:text-4xl tracking-[0.16em] uppercase text-zinc-50">
                  CRISOPEA {p.roman}
                </h1>
              </div>
            </div>

            <p className="mt-8 text-[13px] tracking-[0.2em] uppercase text-zinc-500">{t.format}</p>

            {description
              ? <p className="mt-10 text-[19px] leading-[1.85] text-zinc-300">{description}</p>
              : <p className="mt-10 text-[19px] leading-[1.85] text-zinc-400 italic">{ambiance}</p>}

            {pyramid ? (
              <div className="mt-12 space-y-4">
                <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.pyramid}</p>
                <Row label={t.top} v={pyramid.tete} />
                <Row label={t.heart} v={pyramid.coeur} />
                <Row label={t.base} v={pyramid.fond} />
              </div>
            ) : (
              <p className="mt-8 text-zinc-500 italic">{t.unpublished}</p>
            )}

            <p className="mt-10 text-zinc-500">{t.perfumer}</p>

            <div className="mt-12 border-t pt-10" style={{ borderColor: "rgba(201,168,106,0.18)" }} data-testid="product-status">
              {p.available ? (
                <>
                  <div className="flex items-baseline gap-5">
                    <span className="text-2xl" style={{ color: OR }}>{p.price} €</span>
                    <span className="text-[13px] tracking-[0.2em] uppercase text-zinc-500">{t.available}</span>
                  </div>
                  <AddToCart className="mt-8" product={{ reference: p.reference, label: `CRISOPEA ${p.roman}`, collection: "Crisopea", price: p.price, url: `/crisopea/${p.slug}` }} />
                </>
              ) : (
                <>
                  <p className="text-[13px] tracking-[0.2em] uppercase text-zinc-500">{t.soldOut}</p>
                  <p className="mt-5 max-w-md text-[15px] leading-[1.75] text-zinc-500">{t.soldOutNote}</p>
                  <NotifyForm productRef={`CRISOPEA ${p.roman}`} t={t} />
                </>
              )}
            </div>

            <p className="mt-10 text-[13px] tracking-[0.14em] uppercase text-zinc-600">{t.made}</p>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, v }) => (
  <div className="flex gap-6">
    <span className="w-16 shrink-0 text-[13px] tracking-[0.14em] uppercase text-zinc-600">{label}</span>
    <span className="text-zinc-300">{v}</span>
  </div>
);
